import { KeyValues } from "@arkads/shared/dist/v2";
import { ResolvedVideoConfig } from "../../types/config";
import { IAdsService } from "../../types/IAdsService";
import { IConfig } from "../../types/IConfig";
import type { JarvisResponse } from "../jarvis-service";
import { IBid, IProvider } from "../provider";
import { normalizePosition } from "../../utils/position";

export const USA_TODAY_MCM_ID = "7103";
export const USA_TODAY_SCHAIN_ID = "ark-0012";

export function getUsaTodayAuctionUnit(
  hostname: string,
  pathname: string,
): string {
  if (/uclick-crossword/.test(pathname)) {
    return "games.usatoday.com/uclick_crossword";
  }
  if (/(uclick-|amu-)/.test(pathname)) {
    return "games.usatoday.com/uclick_other";
  }
  if (/games\.usatoday\.com/.test(hostname)) {
    return "games.usatoday.com/arkadium";
  }
  return "usatoday.com/arkadium";
}

/**
 * Preserves the fixed USA Today supply-chain identity and route-specific video
 * inventory while reusing the standard Jarvis, Prebid, Amazon, and fallback
 * providers.
 */
export class UsaTodayAdsService implements IAdsService {
  protected network = '100151972';
  protected mcm = USA_TODAY_MCM_ID;
  protected auctionUnit = getUsaTodayAuctionUnit;
  protected keyValues = new KeyValues(() => document.location.href, window.localStorage, () => document.cookie);

  public request(config: IConfig) {
    const elementId = normalizePosition(config);
    const url = top?.location.href || location.href;

    this.keyValues.setFromConfig(config.keyvalues);
    this.keyValues.setKeyValue('product', config.product || 'unknown');
    this.keyValues.setKeyValue('elementId', elementId);

    const vastUrl = 'https://pubads.g.doubleclick.net/gampad/ads?iu=' + this.getIuBase() +
      '&sz=1x1%7C400x300%7C300x400%7C640x480' +
      '&cust_params=' + this.keyValues.getUrlParam({ retryOnError: '2' }) +
      '&impl=s' +
      '&gdfp_req=1' +
      '&env=vp' +
      '&plcmt=1' +
      '&output=vast' +
      '&unviewed_position_start=1' +
      '&vad_type=linear' +
      '&url=' + url +
      '&description_url=' + url +
      '&correlator=' + parseInt(Math.random() * 1e15 + '');

    return Promise.resolve([this.createBid(vastUrl)]);
  }

  public cleanup() {

  }
  
  protected resolveAuctionConfig(config: JarvisResponse): JarvisResponse {
    return {
      ...config,
      mcm: this.mcm,
      schain: USA_TODAY_SCHAIN_ID,
    };
  }

  protected getIuBase(): string {
    const { hostname, pathname } = window.location;
    return `/${this.network},${this.mcm}/${this.auctionUnit(
      hostname,
      pathname,
    )}`;
  }

  protected createBid(url: string) {
    return {
      bid: { cpm: 0 },
      provider: this.createStubProvider(url),
    };
  }

  protected createStubProvider(url: string): IProvider {
    return {
      name: 'usat',
      configure() {},
      requestBids: () => Promise.resolve([]),
      buildVideoUrl: () => Promise.resolve(url),
      cleanup: () => null,
      setAdUnit: () => null,
    };
  }
}
